import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserDataForDate, TIME_SLOTS } from '../services/firestoreService';

const CalendarView = () => {
  const { user } = useParams();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthlyData, setMonthlyData] = useState({});
  const [loading, setLoading] = useState(true);

  // Get calendar data for a specific month
  const getMonthData = async (year, month) => {
    const data = {};
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    setLoading(true);
    
    try {
      // Fetch data for each day of the month
      const promises = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        promises.push(getUserDataForDate(user, dateStr));
      }
      
      const results = await Promise.all(promises);
      
      // Store results with day as key
      results.forEach((dayData, index) => {
        const day = index + 1;
        data[day] = dayData;
      });
      
      setMonthlyData(data);
    } catch (error) {
      console.error('Error fetching month data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate color for a specific date based on completed slots
  const getDateColor = (dayData) => {
    if (!dayData) return 'bg-gray-100 hover:bg-gray-200'; // Default color
    
    let completedSlots = 0;
    TIME_SLOTS.forEach(slot => {
      if (dayData[slot] && dayData[slot].status === 'done') {
        completedSlots++;
      }
    });

    // Color logic based on requirements
    if (completedSlots < 2) {
      return 'bg-red-200 hover:bg-red-300'; // Red for less than 2
    } else if (completedSlots === 2) {
      return 'bg-yellow-300 hover:bg-yellow-400'; // Light yellow for exactly 2
    } else if (completedSlots === 3) {
      return 'bg-yellow-600 hover:bg-yellow-700'; // Dark yellow for exactly 3
    } else if (completedSlots === 4) {
      return 'bg-green-300 hover:bg-green-400'; // Light green for exactly 4
    } else if (completedSlots > 4) {
      return 'bg-green-600 hover:bg-green-700'; // Dark green for more than 4
    } else {
      return 'bg-gray-100 hover:bg-gray-200'; // Default fallback
    }
  };

  // Handle date click
  const handleDateClick = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    navigate(`/daily/${user}/${dateStr}`);
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  // Get days in month and first day of month
  const getDaysInMonth = () => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = () => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  };

  // Load data when month changes
  useEffect(() => {
    getMonthData(currentDate.getFullYear(), currentDate.getMonth());
  }, [currentDate, user]);

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth();
    const firstDay = getFirstDayOfMonth();
    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-20"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = monthlyData[day];
      const colorClass = getDateColor(dayData);
      
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`h-20 rounded-lg border border-gray-200 p-2 text-left 
                     transition-all duration-200 transform hover:scale-105 
                     ${colorClass} ${loading ? 'opacity-50' : ''}`}
        >
          <div className="font-semibold text-gray-800">{day}</div>
          {dayData && (
            <div className="text-xs text-gray-600 mt-1">
              {(() => {
                let completed = 0;
                TIME_SLOTS.forEach(slot => {
                  if (dayData[slot] && dayData[slot].status === 'done') completed++;
                });
                return `${completed}/7 done`;
              })()}
            </div>
          )}
        </button>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-800 font-medium"
            >
              ← Back to Users
            </button>
            <h1 className="text-3xl font-bold text-gray-800">
              {user}'s Work Calendar
            </h1>
            <div className="w-20"></div> {/* Spacer for centering */}
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
            >
              ←
            </button>
            <h2 className="text-xl font-semibold text-gray-700">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
            >
              →
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="text-gray-500">Loading calendar data...</div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-semibold text-gray-600 text-sm">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-2">
              {generateCalendarDays()}
            </div>

            {/* Legend */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Color Legend:</h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-200 rounded"></div>
                  <span className="text-gray-600">&lt;2 slots done</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-300 rounded"></div>
                  <span className="text-gray-600">2 slots done</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-600 rounded"></div>
                  <span className="text-gray-600">3 slots done</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-300 rounded"></div>
                  <span className="text-gray-600">4 slots done</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-600 rounded"></div>
                  <span className="text-gray-600">&gt;4 slots done</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarView;
